import { handlingBreakdown } from '../shared/purchase-order-costs';
import { A11yModule } from '@angular/cdk/a11y';
import { Component, Input, Output, OnInit, EventEmitter, OnChanges, OnDestroy, HostListener, SimpleChanges } from '@angular/core';
import { RmsModule } from '../../../../../custom-modules/rms-module/rms.module';

export interface CalcData {
  // which modal to render
  Decaf: boolean;

  // --- Common (non-decaf) ---
  WorstCase?: boolean;
  TariffInPrice?: boolean;
  TariffAmount?: number;

  // context for bottom “Used” field
  ShippingTerm?: number;           // 1..n; 4 means "None"
  ShipmentTypeID?: number;         // 1 = FOBEX else Ex-Whse
  FOBtoExWarehouseValueTemp?: number | null;
  ExWhseCost?: number | null;

  // weights / bags
  netWeight?: number;              // total lbs
  grossWeight?: number;
  totalBags?: number;

  // base cost (per-lb cost or Fx+Diff result that upstream gave you)
  cost?: number;

  // port / fees
  steamShipRate?: number;
  marineInsurance?: number;        // percent-of-value (0.17 -> 0.17%)
  dutyAndEntry?: number;
  drayage?: number;
  stripping?: number;
  storage?: number;
  weighing?: number;
  financing?: number;              // annual %, from port meta (disabled here)
  fot?: number;
  sampleCharge?: number;
  rfa?: number;
  rateBuffer?: number;
  finalCostWith?: number;          // RNY buffer
  DaysToFinance?: number;          // 30, 60, 90...

  // derived (we’ll recalc, but accept incoming)
  totalCost?: number;
  steamShipRatePerPound?: number;
  marineInsurancePerPound?: number;
  dutyAndEntryPerPound?: number;
  drayagePerPound?: number;
  strippingPerPound?: number;
  storagePerPound?: number;
  weighingPerPound?: number;
  financingPerPound?: number;
  fotPerPound?: number;
  sampleChargePerPound?: number;
  rfaPerPound?: number;
  totalEstimate?: number;

  // --- Decaf ---
  DecaffeinatorID?: number;        // used to show “Seaforth”
  DecafCostPerPound?: number;      // default 0.76
  DecafCostTotal?: number;
  decafSeaforth?: number;          // auto = 800 if Seaforth (6815)
  decafStripping?: number;
  decafStrippingPerBag?: number;   // default 1.10
  decafStorage?: number;
  decafStoragePerBag?: number;     // default 0.79
  decafWeighing?: number;
  decafWeighingPerBag?: number;    // default 0.63
  decafLossValue?: number;         // incoming or computed upstream
  decafLossValuePercent?: number;  // default 6.25
  totalDecafLossValue?: number;
  decafFinancing?: number;         // incoming or computed upstream
  decafFinancingPercent?: number;  // default 10.00
  totalDecafNetWeight?: number;
  totalDecafNetWeightFromLoss?: number;
  decafBuffer?: number;            // default 0.03
  decafTolling?: number;
  decafTollingPerPound?: number;
  totalOrderWithDecafCost?: number;
  decafTrucking?: number;
  tollingCost?: number;
  parentBaseFinalPerLb?: number;
  parentNetWeight?: number;
  totalCommissions?: number;

  /** Child bag count to use for per-bag decaf fees. Falls back to `totalBags`. */
  childBagsOrdered?: number;

  /** For child result labels (to match your forms exactly): */
  parentPricePound?: number | null;
  parentDifferentialPerPound?: number | null;
  parentFixationCurrentLevel?: number | null;

  /** Derived child results (emitted back out): */
  finalWarehouseInCostChild?: number;
  finalWarehouseInDifferentialChild?: number;
  estimatedCostChild?: number;
}

@Component({
  selector: 'app-calc-form',
  imports: [RmsModule, A11yModule],
  templateUrl: './calc-form.component.html',
  styleUrl: './calc-form.component.scss'
})
export class CalcFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input() calcData!: CalcData;
  @Output() onDataChange = new EventEmitter<CalcData>();
  @Output() close = new EventEmitter<void>();

  // render switch
  Decaf = false;

  // ====== Common ======
  WorstCase = false;
  TariffInPrice = false;
  TariffAmount = 0;
  ShippingTerm = 4;
  ShipmentTypeID = 1;
  FOBtoExWarehouseValueTemp: number | null = null;
  ExWhseCost: number | null = null;

  netWeight = 0;
  grossWeight = 0;
  totalBags = 0;

  cost = 0; // base per lb

  steamShipRate = 0;
  marineInsurance = 0.17;
  dutyAndEntry = 0;
  drayage = 0;
  stripping = 0;
  storage = 0;
  weighing = 0;
  financing = 0;
  fot = 0;
  sampleCharge = 0;
  rfa = 0;
  rateBuffer = 0;
  finalCostWith = 0.015;
  DaysToFinance = 30;

  totalCost = 0;
  steamShipRatePerPound = 0;
  marineInsurancePerPound = 0;
  dutyAndEntryPerPound = 0;
  drayagePerPound = 0;
  strippingPerPound = 0;
  storagePerPound = 0;
  weighingPerPound = 0;
  financingPerPound = 0;
  fotPerPound = 0;
  sampleChargePerPound = 0;
  rfaPerPound = 0;
  totalEstimate = 0;

  // ====== Decaf ======
  DecaffeinatorID: number | undefined;
  DecafCostPerPound = 0.76;
  DecafCostTotal = 0;
  decafSeaforth = 0;
  decafStripping = 0;
  decafStrippingPerBag = 1.10;
  decafStorage = 0;
  decafStoragePerBag = 0.79;
  decafWeighing = 0;
  decafWeighingPerBag = 0.63;
  decafLossValue = 0;
  decafLossValuePercent = 6.25;
  totalDecafLossValue = 0;
  decafFinancing = 0;
  decafFinancingPercent = 10.00;
  totalDecafNetWeight = 0;
  totalDecafNetWeightFromLoss = 0;
  decafBuffer = 0.03;
  decafTolling = 0;
  decafTollingPerPound = 0;
  totalOrderWithDecafCost = 0;
  decafTrucking = 0;
  tollingCost = 0;
  parentBaseFinalPerLb = 0;
  parentNetWeight: number | null = null;
  totalCommissions = 0;

  childBagsOrdered: number | null = null;
  parentPricePound: number | null = null;
  parentDifferentialPerPound: number | null = null;
  parentFixationCurrentLevel: number | null = null;

  // optionally expose derived child labels (not required for UI)
  finalWarehouseInCostChild: number | null = null;
  finalWarehouseInDifferentialChild: number | null = null;
  estimatedCostChild: number | null = null;

  // UI
  closingTotals = false;

  // select options
  DaysToFinanceList = [
    { name: '30 Days', value: 30 },
    { name: '60 Days', value: 60 },
    { name: '90 Days', value: 90 },
  ];

  private debounceId: any = null;
  private decafDebounceId: any = null;

  ngOnInit() {}
  ngOnDestroy() { clearTimeout(this.debounceId); clearTimeout(this.decafDebounceId); }
  @HostListener('document:keydown.escape') cancel() { this.close.emit(); }
  apply() {
    clearTimeout(this.debounceId); clearTimeout(this.decafDebounceId);
    this.recalculateCosts(false);
    this.recalculateDecafCosts(false);
    this.emitChange();
    this.close.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['calcData']) this.hydrateFromInput();
  }

  // ---------- Hydration ----------
  private hydrateFromInput(): void {
    const d = this.calcData ?? {} as CalcData;

    // which popup
    this.Decaf = !!d.Decaf;

    // common
    this.WorstCase = !!d.WorstCase;
    this.TariffInPrice = !!d.TariffInPrice;
    this.TariffAmount = Number(d.TariffAmount ?? 0);
    this.ShippingTerm = d.ShippingTerm ?? this.ShippingTerm;
    this.ShipmentTypeID = d.ShipmentTypeID ?? this.ShipmentTypeID;
    this.FOBtoExWarehouseValueTemp = d.FOBtoExWarehouseValueTemp ?? null;
    this.ExWhseCost = d.ExWhseCost ?? null;

    this.netWeight = d.netWeight ?? this.netWeight;
    this.grossWeight = d.grossWeight ?? this.grossWeight;
    this.totalBags = d.totalBags ?? this.totalBags;

    this.cost = d.cost ?? this.cost;

    this.steamShipRate = d.steamShipRate ?? this.steamShipRate;
    this.marineInsurance = d.marineInsurance ?? this.marineInsurance;
    this.dutyAndEntry = d.dutyAndEntry ?? this.dutyAndEntry;
    this.drayage = d.drayage ?? this.drayage;
    this.stripping = d.stripping ?? this.stripping;
    this.storage = d.storage ?? this.storage;
    this.weighing = d.weighing ?? this.weighing;
    this.financing = d.financing ?? this.financing;
    this.fot = d.fot ?? this.fot;
    this.sampleCharge = d.sampleCharge ?? this.sampleCharge;
    this.rfa = d.rfa ?? this.rfa;
    this.rateBuffer = d.rateBuffer ?? this.rateBuffer;
    this.finalCostWith = d.finalCostWith ?? this.finalCostWith;
    this.DaysToFinance = d.DaysToFinance ?? this.DaysToFinance;

    // decaf
    this.DecaffeinatorID = d.DecaffeinatorID;
    this.DecafCostPerPound = d.DecafCostPerPound ?? this.DecafCostPerPound;
    this.DecafCostTotal = d.DecafCostTotal ?? this.DecafCostTotal;
    this.decafSeaforth = d.decafSeaforth ?? (this.DecaffeinatorID === 6815 ? 800 : 0);
    this.decafStripping = d.decafStripping ?? this.decafStripping;
    this.decafStrippingPerBag = d.decafStrippingPerBag ?? this.decafStrippingPerBag;
    this.decafStorage = d.decafStorage ?? this.decafStorage;
    this.decafStoragePerBag = d.decafStoragePerBag ?? this.decafStoragePerBag;
    this.decafWeighing = d.decafWeighing ?? this.decafWeighing;
    this.decafWeighingPerBag = d.decafWeighingPerBag ?? this.decafWeighingPerBag;
    this.decafLossValue = d.decafLossValue ?? this.decafLossValue;
    this.decafLossValuePercent = d.decafLossValuePercent ?? this.decafLossValuePercent;
    this.totalDecafLossValue = d.totalDecafLossValue ?? this.totalDecafLossValue;
    this.decafFinancing = d.decafFinancing ?? this.decafFinancing;
    this.decafFinancingPercent = d.decafFinancingPercent ?? this.decafFinancingPercent;
    this.totalDecafNetWeight = d.totalDecafNetWeight ?? this.totalDecafNetWeight;
    this.totalDecafNetWeightFromLoss = d.totalDecafNetWeightFromLoss ?? this.totalDecafNetWeightFromLoss;
    this.decafBuffer = d.decafBuffer ?? this.decafBuffer;
    this.decafTolling = d.decafTolling ?? this.decafTolling;
    this.decafTollingPerPound = d.decafTollingPerPound ?? this.decafTollingPerPound;
    this.totalOrderWithDecafCost = d.totalOrderWithDecafCost ?? this.totalOrderWithDecafCost;
    this.decafTrucking = d.decafTrucking ?? this.decafTrucking;
    this.tollingCost = d.tollingCost ?? this.tollingCost;

    this.parentBaseFinalPerLb = Number(d.parentBaseFinalPerLb ?? this.parentBaseFinalPerLb);
    this.parentNetWeight = (d.parentNetWeight ?? this.parentNetWeight) as number | null;
    this.totalCommissions = Number(d.totalCommissions ?? this.totalCommissions);
    this.childBagsOrdered = (d.childBagsOrdered ?? this.childBagsOrdered) as number | null;

    this.parentPricePound = (d.parentPricePound ?? this.parentPricePound) as number | null;
    this.parentDifferentialPerPound = (d.parentDifferentialPerPound ?? this.parentDifferentialPerPound) as number | null;
    this.parentFixationCurrentLevel = (d.parentFixationCurrentLevel ?? this.parentFixationCurrentLevel) as number | null;

    // first compute
    this.recalculateCosts(false);
    this.recalculateDecafCosts(false);
  }

  // ---------- Derived / emit ----------
  recalculateCostsDebounced() {
    clearTimeout(this.debounceId);
    this.debounceId = setTimeout(() => this.recalculateCosts(false), 250);
  }
  recalculateDecafCostsDebounced() {
    clearTimeout(this.decafDebounceId);
    this.decafDebounceId = setTimeout(() => this.recalculateDecafCosts(false), 250);
  }

  private recalculateCosts(emit: boolean) {
    const safeNet = Math.max(1, Number(this.netWeight || 0));
    const bags = Number(this.totalBags || 0);
    const base = Number(this.cost || 0);

    this.totalCost = base * safeNet;

    Object.assign(this, handlingBreakdown(this));

    if (emit) this.emitChange();
  }

  private recalculateDecafCosts(emit: boolean) {
    // Parent pre-loss net (lbs); fall back to current netWeight
    const preLossNet = Math.max(1, Number((this.parentNetWeight ?? this.netWeight) || 0));
    // CHILD bags for per-bag fees (fall back to totalBags)
    const bags = Number((this.childBagsOrdered ?? this.totalBags) || 0);

    // Parent base final $/lb for loss + financing
    const baseFinal = Number(this.parentBaseFinalPerLb || 0);

    // ----- 1) Loss and post-loss weight (match your forms) -----
    this.totalDecafNetWeightFromLoss = preLossNet * ((this.decafLossValuePercent || 0) / 100);
    this.totalDecafNetWeight = preLossNet - this.totalDecafNetWeightFromLoss;

    // ----- 2) Per-bag child charges -----
    this.decafStripping = (this.decafStrippingPerBag || 0) * bags;
    this.decafStorage = (this.decafStoragePerBag || 0) * bags;
    this.decafWeighing = (this.decafWeighingPerBag || 0) * bags;

    // ----- 3) Seaforth vendor surcharge -----
    this.decafSeaforth = (this.DecaffeinatorID === 6815) ? 800 : (this.decafSeaforth || 0);
    this.decafLossValuePercent = (this.DecaffeinatorID === 6815) ? 6.25 : this.decafLossValuePercent;
    // ----- 4) Core decaf costs (identical to your form code) -----
    // Decaf cost per pound uses PRE-LOSS (parent) net
    this.DecafCostTotal = (this.DecafCostPerPound || 0) * preLossNet;
    // Loss of value & financing based on PARENT baseFinal and PRE-LOSS net
    this.decafLossValue = (baseFinal * preLossNet) * ((this.decafLossValuePercent || 0) / 100);
    this.decafFinancing = ((baseFinal * preLossNet) * ((this.decafFinancingPercent || 0) / 100)) / 365 * 120;
    console.log("Decaf Base Final: ", baseFinal);

    // (Optional mirror var)
    this.totalDecafLossValue = this.decafLossValue;

    // Roll-up total tolling
    const total =
      (this.DecafCostTotal || 0) +
      (this.decafSeaforth || 0) +
      (this.decafStripping || 0) +
      (this.decafStorage || 0) +
      (this.decafWeighing || 0) +
      (this.decafLossValue || 0) +
      (this.decafFinancing || 0) +
      (this.decafTrucking || 0) +
      (this.totalCommissions || 0);

    this.decafTolling = total;
    this.decafTollingPerPound = (this.parentNetWeight) ? (total / this.parentNetWeight!) : 0;
    // Final combined per-lb (for info): base + toll + buffer
    this.totalOrderWithDecafCost = (this.decafTollingPerPound || 0) + baseFinal + (this.decafBuffer || 0);

    // Match your "tollingCost" = per-lb tolling + commissions
    this.tollingCost = (this.decafTollingPerPound || 0);

    // ----- 5) Derive the same child fields your forms set -----
    // Child.Tolling (per-lb)
    const childTolling = this.decafTollingPerPound;

    // Child.FinalWarehouseInCost = parent.PricePound + child.Tolling + commissions
    this.finalWarehouseInCostChild =
      (Number(this.parentPricePound || 0) + Number(childTolling || 0));

    // Child.FinalWarehouseInDifferential = parent.DiffPerLb + child.Tolling + commissions
    this.finalWarehouseInDifferentialChild =
      (Number(this.parentDifferentialPerPound || 0) + Number(childTolling || 0));

    // Child.EstimatedCost = FinalWarehouseInDifferential + parent.FixationCurrentLevel
    this.estimatedCostChild =
      Number(this.finalWarehouseInDifferentialChild || 0) + Number(this.parentFixationCurrentLevel || 0);

    if (emit) this.emitChange();
  }

  private emitChange() {
    const out: CalcData = {
      Decaf: this.Decaf,
      WorstCase: this.WorstCase,
      ShippingTerm: this.ShippingTerm,
      ShipmentTypeID: this.ShipmentTypeID,
      FOBtoExWarehouseValueTemp: this.FOBtoExWarehouseValueTemp,
      ExWhseCost: this.ExWhseCost,

      netWeight: this.netWeight,
      grossWeight: this.grossWeight,
      totalBags: this.totalBags,

      cost: this.cost,

      steamShipRate: this.steamShipRate,
      marineInsurance: this.marineInsurance,
      dutyAndEntry: this.dutyAndEntry,
      drayage: this.drayage,
      stripping: this.stripping,
      storage: this.storage,
      weighing: this.weighing,
      financing: this.financing,
      fot: this.fot,
      sampleCharge: this.sampleCharge,
      rfa: this.rfa,
      rateBuffer: this.rateBuffer,
      finalCostWith: this.finalCostWith,
      DaysToFinance: this.DaysToFinance,

      totalCost: this.totalCost,
      steamShipRatePerPound: this.steamShipRatePerPound,
      marineInsurancePerPound: this.marineInsurancePerPound,
      dutyAndEntryPerPound: this.dutyAndEntryPerPound,
      drayagePerPound: this.drayagePerPound,
      strippingPerPound: this.strippingPerPound,
      storagePerPound: this.storagePerPound,
      weighingPerPound: this.weighingPerPound,
      financingPerPound: this.financingPerPound,
      fotPerPound: this.fotPerPound,
      sampleChargePerPound: this.sampleChargePerPound,
      rfaPerPound: this.rfaPerPound,
      totalEstimate: this.totalEstimate,

      DecaffeinatorID: this.DecaffeinatorID,
      DecafCostPerPound: this.DecafCostPerPound,
      DecafCostTotal: this.DecafCostTotal,
      decafSeaforth: this.decafSeaforth,
      decafStripping: this.decafStripping,
      decafStrippingPerBag: this.decafStrippingPerBag,
      decafStorage: this.decafStorage,
      decafStoragePerBag: this.decafStoragePerBag,
      decafWeighing: this.decafWeighing,
      decafWeighingPerBag: this.decafWeighingPerBag,
      decafLossValue: this.decafLossValue,
      decafLossValuePercent: this.decafLossValuePercent,
      totalDecafLossValue: this.totalDecafLossValue,
      decafFinancing: this.decafFinancing,
      decafFinancingPercent: this.decafFinancingPercent,
      totalDecafNetWeight: this.totalDecafNetWeight,
      totalDecafNetWeightFromLoss: this.totalDecafNetWeightFromLoss,
      decafBuffer: this.decafBuffer,
      decafTolling: this.decafTolling,
      decafTollingPerPound: this.decafTollingPerPound,
      totalOrderWithDecafCost: this.totalOrderWithDecafCost,
      decafTrucking: this.decafTrucking,
      tollingCost: this.tollingCost,

      // derived child results (match your form setters)
      finalWarehouseInCostChild: this.finalWarehouseInCostChild ?? undefined,
      finalWarehouseInDifferentialChild: this.finalWarehouseInDifferentialChild ?? undefined,
      estimatedCostChild: this.estimatedCostChild ?? undefined,
    };
    this.onDataChange.emit(out);
  }

  // ---------- UI helpers ----------
  toggleTotals() { this.close.emit(); }
  toggleDecafTotals() { this.close.emit(); }

  get usedHandlingCost(): number {
    if (this.ShippingTerm === 4) return Number(this.ExWhseCost ?? 0);
    return (this.ShipmentTypeID === 1)
      ? Number(this.FOBtoExWarehouseValueTemp ?? 0)
      : Number(this.ExWhseCost ?? 0);
  }
  get usedHandlingLabel(): string {
    if (this.ShippingTerm === 4) return 'Handling Cost (None)';
    return (this.ShipmentTypeID === 1) ? 'FOBEXWhse-Temp (Used)' : 'Ex-Whse Cost (Used)';
  }
}
